import React, { Component, ReactNode } from 'react';
import { AccessBase } from '../../domain/access/BaseAccess';
import AccessListComponent from './AccessListComponent';
import AccessCreatorComponent from './AccessCreatorComponent';
import { query, repository, subscribe } from '@trlogic/client-js';
import CollectionNames from '../../constant/CollectionNames';
import { Group, User } from '../../domain/rbac/RbacDomains';
import { InstanceContainer } from '../../config/binding/InstanceBinding';
import Injectables from '../../constant/Injectables';
import CustomLoading from '../custom/CustomLoading';
import EmptyUserComponent from '../custom/empty-components/EmptyUserComponent';

class AccessContainer extends Component<Props, State> {
  public state = new State();

  private usersSubscription: subscribe.Subscription | undefined;
  private groupSubscription: subscribe.Subscription | undefined;
  private computation: subscribe.Computation | undefined;

  private repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);

  public render(): ReactNode {
    const { access, changeAccess, disabled } = this.props;
    const { users, groups, loading } = this.state;
    if (loading) {
      return <CustomLoading />;
    }
    return (
      <div className={'flex flex-col w-full h-full items-center gap-y-3 py-5 px-5'}>
        <AccessCreatorComponent users={users} groups={groups} access={access} changeAccess={changeAccess} disabled={disabled} />
        <div key={'access'} className={'flex w-full h-full'}>
          {access.collaborators && access.collaborators.length > 0 ? (
            <AccessListComponent users={users} changeAccess={this.checkAccessChange} access={access} disabled={disabled} />
          ) : (
            <EmptyUserComponent />
          )}
        </div>
      </div>
    );
  }

  public async componentDidMount() {
    await this.startTracker();
  }

  public async componentWillUnmount() {
    await this.stopTracker();
  }

  private startTracker = async () => {
    const usersQuery = query.QueryBuilder.select().select('username', 'firstName', 'lastName').from(CollectionNames.USERS);
    const groupQuery = query.QueryBuilder.select().from(CollectionNames.GROUPS);

    this.usersSubscription = subscribe.Formica.subscribe(CollectionNames.USERS, usersQuery.build());
    this.groupSubscription = subscribe.Formica.subscribe(CollectionNames.GROUPS, groupQuery.build());

    this.computation = subscribe.Tracker.autorun(this.tracker, [this.usersSubscription, this.groupSubscription]);
  };

  private stopTracker = async () => {
    await this.usersSubscription?.stop();
    await this.groupSubscription?.stop();
    this.computation?.stop();
  };

  private tracker = async () => {
    if (this.computation?.ready()) {
      await this.buildData();
    }
  };

  private buildData = async () => {
    const userQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.USERS).build();
    const users: User[] = await this.repository.findMany(userQuery);
    const groupQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.GROUPS).build();
    const groups: Group[] = await this.repository.findMany(groupQuery);
    this.setState({ groups: groups, users: users, loading: false });
  };

  public checkAccessChange = (access: AccessBase) => {
    const { changeAccess, disabled } = this.props;
    if (disabled) return;
    changeAccess(access);
  };
}

interface Props {
  access: AccessBase;
  changeAccess: (access: AccessBase) => void;
  disabled?: boolean;
}

class State {
  users: User[] = [];
  groups: Group[] = [];
  loading: boolean = true;
}

export default AccessContainer;
